// Beta-0.5D-A · 書類の種類と読み取り設定 — Document Profile UI.
// Admins may edit: auto_save_min, review_min, required_fields, and reset
// overrides. Schema JSON is intentionally NOT editable through this UI.

import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ArrowsClockwise, Warning, Info, CircleNotch, ArrowsClockwise as Reset,
  Check, PencilSimple, X,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import FieldMeaningsSection from './FieldMeaningsSection';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const pct = (n) => (typeof n === 'number' ? `${Math.round(n * 100)}%` : '—');

function HealthDot({ rate, thresholds }) {
  // rate is 0..1; higher = worse for failure/review, better for autosave.
  // caller decides color via thresholds:{good:0.7, warn:0.4} or reversed.
  if (rate === null || rate === undefined) return <span className="text-zinc-300">—</span>;
  const isReversed = thresholds.reversed;
  const good = isReversed ? rate <= thresholds.good : rate >= thresholds.good;
  const warn = !good && (isReversed ? rate <= thresholds.warn : rate >= thresholds.warn);
  const cls = good ? 'bg-emerald-500' : warn ? 'bg-amber-500' : 'bg-rose-500';
  return <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${cls}`} />;
}

// `GET/PUT /api/doc-ai/profiles` returns fields as objects
// ({ key, label_ja, required }). Older builds sent tuples, so accept both and
// never destructure blindly — doing so blanked this screen.
const normaliseFields = (fields) => (Array.isArray(fields) ? fields : [])
  .map((f) => (Array.isArray(f)
    ? { key: f[0], label_ja: f[1], required: !!f[2] }
    : { key: f?.key, label_ja: f?.label_ja ?? f?.label ?? f?.key, required: !!f?.required }))
  .filter((f) => f.key);

function ProfileRow({ p, onEdit, onReset, onLoadHealth, health }) {
  const overridden = p.has_override;
  const requiredKeys = normaliseFields(p.fields).filter((f) => f.required).map((f) => f.key);
  return (
    <div className="rounded-md border border-zinc-200 bg-white px-4 py-3"
      data-testid={`profile-row-${p.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-zinc-900">
            {p.label_ja || p.key}
            {overridden && (
              <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9.5px] font-semibold uppercase tracking-wider">
                管理者上書き
              </span>
            )}
          </p>
          <p className="text-[11px] text-zinc-500 mt-0.5 font-mono">{p.key}</p>
          <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1 text-[11.5px]">
            <div><span className="text-zinc-500 whitespace-nowrap">分類: </span><span className="text-zinc-900">{p.family_label || p.family_label_ja || p.family}</span></div>
            <div><span className="text-zinc-500 whitespace-nowrap">読み取り方式: </span><span className="text-zinc-900">{p.extraction || '—'}</span></div>
            <div><span className="text-zinc-500 whitespace-nowrap">自動保存: </span><span className="text-zinc-900 tabular-nums">{p.auto_save_min}%</span></div>
            <div><span className="text-zinc-500 whitespace-nowrap">要確認: </span><span className="text-zinc-900 tabular-nums">{p.review_min}%</span></div>
          </div>
          {requiredKeys.length > 0 && (
            <p className="mt-1 text-[11px] text-zinc-500">
              必須項目: {requiredKeys.map((f) => (
                <span key={f} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-zinc-100 font-mono text-[10.5px]">{f}</span>
              ))}
            </p>
          )}
          {health && (
            <div className="mt-2 rounded border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1"
              data-testid={`profile-health-${p.key}`}>
              <div><span className="text-zinc-500">処理件数: </span>
                <span className="tabular-nums font-semibold">{health.documents_processed.toLocaleString('ja-JP')}</span></div>
              <div><span className="text-zinc-500">平均信頼度: </span>
                <span className="tabular-nums font-semibold">
                  <HealthDot rate={health.avg_confidence} thresholds={{good:0.8, warn:0.6}} />
                  {pct(health.avg_confidence)}</span></div>
              <div><span className="text-zinc-500">要確認率: </span>
                <span className="tabular-nums font-semibold">
                  <HealthDot rate={health.review_rate} thresholds={{good:0.2, warn:0.4, reversed:true}} />
                  {pct(health.review_rate)}</span></div>
              <div><span className="text-zinc-500">自動保存率: </span>
                <span className="tabular-nums font-semibold">
                  <HealthDot rate={health.auto_save_rate} thresholds={{good:0.6, warn:0.3}} />
                  {pct(health.auto_save_rate)}</span></div>
              <div><span className="text-zinc-500">失敗率: </span>
                <span className="tabular-nums font-semibold">
                  <HealthDot rate={health.failure_rate} thresholds={{good:0.05, warn:0.15, reversed:true}} />
                  {pct(health.failure_rate)}</span></div>
              <div><span className="text-zinc-500">最終使用: </span>
                <span className="tabular-nums text-zinc-700 text-[10.5px]">
                  {health.last_used ? new Date(health.last_used).toLocaleDateString('ja-JP') : '—'}</span></div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!health && (
            <Button size="sm" variant="outline" onClick={() => onLoadHealth(p.key)}
              className="h-8 text-[11px]"
              data-testid={`profile-health-load-${p.key}`}>
              健全性を表示
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onEdit(p)}
            className="h-8 text-[11px]"
            data-testid={`profile-edit-${p.key}`}>
            <PencilSimple size={11} className="mr-1" /> 編集
          </Button>
          {overridden && (
            <Button size="sm" variant="outline" onClick={() => onReset(p)}
              className="h-8 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid={`profile-reset-${p.key}`}>
              <Reset size={11} className="mr-1" /> 既定に戻す
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function EditDialog({ profile, onClose, onSaved }) {
  const [autoSave, setAutoSave] = useState(profile?.auto_save_min ?? 85);
  const [review, setReview] = useState(profile?.review_min ?? 60);
  const allFields = normaliseFields(profile?.fields);
  const [required, setRequired] = useState(() => new Set(
    profile?.required_fields || allFields.filter((f) => f.required).map((f) => f.key)));
  const [busy, setBusy] = useState(false);

  const toggleReq = (k) => {
    const next = new Set(required);
    if (next.has(k)) next.delete(k); else next.add(k);
    setRequired(next);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        auto_save_min: Number(autoSave),
        review_min: Number(review),
        required_fields: Array.from(required),
      };
      const r = await axios.put(`${API}/api/doc-ai/profiles/${profile.key}`, body,
        { headers: authHeader() });
      toast.success('プロファイルを保存しました');
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setBusy(false); }
  };

  if (!profile) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="profile-edit-dialog" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[13px] font-semibold text-zinc-900">{profile.label_ja || profile.key}</p>
            <p className="text-[10.5px] text-zinc-500 font-mono">{profile.key}</p>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            aria-label="閉じる" title="閉じる"
            data-testid="profile-edit-cancel-x"><X size={14} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] font-semibold text-zinc-700">自動保存しきい値 (%)</label>
            <input type="number" min={50} max={100} value={autoSave}
              onChange={(e) => setAutoSave(e.target.value)}
              className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 text-[13px] tabular-nums"
              data-testid="profile-edit-autosave" />
            <p className="text-[10.5px] text-zinc-500 mt-0.5">信頼度がこれ以上のとき自動で確定します。</p>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-zinc-700">要確認しきい値 (%)</label>
            <input type="number" min={0} max={100} value={review}
              onChange={(e) => setReview(e.target.value)}
              className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 text-[13px] tabular-nums"
              data-testid="profile-edit-review" />
            <p className="text-[10.5px] text-zinc-500 mt-0.5">信頼度がこれ未満のとき人手確認へ回します。</p>
          </div>
          {allFields.length > 0 && (
            <div>
              <label className="text-[11px] font-semibold text-zinc-700">必須項目</label>
              <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-zinc-200 bg-white p-2 space-y-1">
                {allFields.map((f) => (
                  <label key={f.key} className="flex min-h-[32px] items-center gap-2 text-[12px] cursor-pointer"
                    data-testid={`profile-edit-req-${f.key}`}>
                    <Checkbox checked={required.has(f.key)}
                      onCheckedChange={() => toggleReq(f.key)}
                      aria-label={`${f.label_ja} を必須にする`} />
                    <span className="font-mono text-[11px] text-zinc-500">{f.key}</span>
                    <span className="text-zinc-700">{f.label_ja}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <p className="text-[10.5px] text-zinc-400">
            <Info size={10} className="inline mr-0.5" />
            スキーマ自体は変更できません（コード規定）。しきい値と必須項目のみ上書き可能です。
          </p>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}
            data-testid="profile-edit-cancel">キャンセル</Button>
          <Button onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800"
            data-testid="profile-edit-save">
            {busy ? <CircleNotch size={11} className="animate-spin mr-1" /> : <Check size={11} className="mr-1" />}
            保存
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function DocumentProfilesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [healthMap, setHealthMap] = useState({});
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/api/doc-ai/profiles`, { headers: authHeader() });
      setData(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || 'プロファイル取得に失敗しました'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadHealth = async (key) => {
    try {
      const r = await axios.get(`${API}/api/doc-ai/profiles/${key}/health`, { headers: authHeader() });
      setHealthMap((h) => ({ ...h, [key]: r.data }));
    } catch (e) { toast.error('健全性の取得に失敗しました'); }
  };

  const doReset = async (p) => {
    if (!window.confirm(`${p.label_ja || p.key} の管理者上書きを削除して、コード規定へ戻します。`)) return;
    try {
      await axios.delete(`${API}/api/doc-ai/profiles/${p.key}/override`, { headers: authHeader() });
      toast.success('コード規定へ戻しました');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'リセットに失敗しました'); }
  };

  const rows = data?.rows || [];
  const byFamily = rows.reduce((acc, p) => {
    (acc[p.family] = acc[p.family] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 space-y-5" data-testid="doc-profiles-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">書類の種類と読み取り設定</h1>
          <p className="text-[12px] text-zinc-600 mt-1">
            AI が書類を読み取るときの判定しきい値と必須項目。スキーマ自体はコードで管理しています。
            {data?.registry_version && (
              <span className="ml-2 text-[10.5px] text-zinc-400 font-mono">
                registry v{data.registry_version}
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="h-9 text-[12px]"
          data-testid="doc-profiles-refresh">
          {loading ? <CircleNotch size={13} className="animate-spin mr-1" />
                    : <ArrowsClockwise size={13} className="mr-1" />} 更新
        </Button>
      </div>

      {err && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800"
          data-testid="doc-profiles-error">
          <Warning size={13} className="inline mr-1" />
          {typeof err === 'string' ? err : (err.message || 'エラー')}
        </div>
      )}

      {Object.entries(byFamily).map(([family, list]) => (
        <div key={family} className="space-y-2" data-testid={`profile-family-${family}`}>
          <h2 className="text-[13px] font-semibold text-zinc-700">
            {data?.families?.[family] || family}
            <span className="ml-2 text-[10.5px] text-zinc-400 font-normal">{list.length} 件</span>
          </h2>
          <div className="space-y-2">
            {list.map((p) => (
              <ProfileRow key={p.key} p={p}
                onEdit={setEditing}
                onReset={doReset}
                onLoadHealth={loadHealth}
                health={healthMap[p.key]} />
            ))}
          </div>
        </div>
      ))}

      {editing && (
        <EditDialog profile={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      <FieldMeaningsSection profiles={rows} />
    </div>
  );
}
